import {
  ApiError,
  type SaasPlanDescriptor,
  getBillingStatus,
  getSaasPlans,
  openCustomerPortal,
  startCheckout,
} from '@/lib/api';
import type { BillingStatus } from '@/lib/types';
import { Check, CreditCard, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

function daysRemaining(isoDate: string): number {
  const end = new Date(isoDate).getTime();
  const now = Date.now();
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatMoneyMinor(minor: number, currency: string): string {
  const amount = minor / 100;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

interface BillingCardProps {
  billing: BillingStatus;
  loading: boolean;
  onManage: () => void;
}

function CurrentStatusCard({ billing, loading, onManage }: BillingCardProps) {
  if (billing.status === 'trial_expired') {
    return (
      <div className="rounded-xl border-2 border-destructive bg-destructive/5 p-6">
        <div className="flex items-start gap-4">
          <CreditCard className="mt-0.5 size-5 shrink-0 text-destructive" strokeWidth={1.75} />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-destructive">Your trial has ended</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your <span className="num">14</span>-day free trial has expired. Pick a plan below to
              continue using the platform.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (billing.status === 'trialing') {
    const days = billing.trialEndsAt ? daysRemaining(billing.trialEndsAt) : 0;
    return (
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="flex items-start gap-4">
          <CreditCard className="mt-0.5 size-5 shrink-0 text-primary" strokeWidth={1.75} />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-foreground">Free trial</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="num font-semibold text-foreground">{days}</span> day
              {days === 1 ? '' : 's'} remaining
              {billing.trialEndsAt ? ` · ends ${formatDate(billing.trialEndsAt)}` : ''}. Pick a plan
              below to subscribe.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (billing.status === 'active') {
    return (
      <div className="rounded-xl border border-border bg-surface p-6">
        <div className="flex items-start gap-4">
          <CreditCard className="mt-0.5 size-5 shrink-0 text-success" strokeWidth={1.75} />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-foreground">Active subscription</h2>
            <dl className="mt-2 grid gap-1 text-sm">
              {billing.planPriceId && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">Plan</dt>
                  <dd className="font-mono text-xs text-foreground">{billing.planPriceId}</dd>
                </div>
              )}
              {billing.currentPeriodEnd && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">Renews</dt>
                  <dd className="text-foreground">{formatDate(billing.currentPeriodEnd)}</dd>
                </div>
              )}
              {billing.cancelAtPeriodEnd && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="text-warning">Cancels at period end</dd>
                </div>
              )}
            </dl>
            <button
              type="button"
              onClick={onManage}
              disabled={loading}
              className="mt-4 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-50"
            >
              {loading ? 'Redirecting…' : 'Manage billing'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // past_due / canceled / incomplete / unpaid / paused
  const warningStatuses: BillingStatus['status'][] = ['past_due', 'unpaid', 'incomplete'];
  const isWarning = warningStatuses.includes(billing.status);

  return (
    <div
      className={`rounded-xl border p-6 ${isWarning ? 'border-warning bg-warning/5' : 'border-border bg-surface'}`}
    >
      <div className="flex items-start gap-4">
        <CreditCard
          className={`mt-0.5 size-5 shrink-0 ${isWarning ? 'text-warning' : 'text-muted-foreground'}`}
          strokeWidth={1.75}
        />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-foreground">
            {billing.status === 'canceled' ? 'Subscription canceled' : 'Billing issue'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {billing.status === 'canceled'
              ? 'Your subscription has been canceled. Pick a plan below to resubscribe.'
              : 'There is an issue with your billing. Please update your payment details.'}
          </p>
          {billing.status !== 'canceled' && (
            <button
              type="button"
              onClick={onManage}
              disabled={loading}
              className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Redirecting…' : 'Update billing'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: SaasPlanDescriptor;
  isCurrent: boolean;
  onSubscribe: (priceId: string) => void;
  loading: boolean;
}

function PlanCard({ plan, isCurrent, onSubscribe, loading }: PlanCardProps) {
  const isPro = plan.tier === 'pro';
  return (
    <div
      className={`relative flex flex-col rounded-xl border p-6 ${
        isPro ? 'border-primary bg-primary/[0.02]' : 'border-border bg-surface'
      }`}
    >
      {isPro && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
          <Sparkles className="size-3" strokeWidth={2.5} />
          Most popular
        </div>
      )}
      <div>
        <h3 className="text-base font-semibold text-foreground">{plan.name}</h3>
        <p className="mt-2">
          <span className="num text-3xl font-medium tracking-tight text-foreground">
            {formatMoneyMinor(plan.monthlyMinor, plan.currency)}
          </span>
          <span className="ml-1 text-sm text-muted-foreground">/ month</span>
        </p>
        <p className="mt-2 text-xs leading-snug text-muted-foreground">{plan.unitDescription}</p>
      </div>

      <ul className="mt-5 flex-1 space-y-2 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 size-4 shrink-0 text-success" strokeWidth={2} />
            <span className="text-foreground/85">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        {plan.priceId === null ? (
          <button
            type="button"
            disabled
            className="w-full rounded-lg border border-dashed border-border bg-secondary/50 px-4 py-2 text-sm font-medium text-muted-foreground"
            title="Operator must set the matching STRIPE_PRICE_* secret"
          >
            Not configured
          </button>
        ) : isCurrent ? (
          <button
            type="button"
            disabled
            className="w-full rounded-lg border border-success/40 bg-success/5 px-4 py-2 text-sm font-medium text-success"
          >
            Current plan
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onSubscribe(plan.priceId!)}
            disabled={loading}
            className={
              isPro
                ? 'w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50'
                : 'w-full rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary disabled:opacity-50'
            }
          >
            {loading ? 'Redirecting…' : `Subscribe to ${plan.name}`}
          </button>
        )}
      </div>
    </div>
  );
}

export function BillingSettings() {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [plans, setPlans] = useState<SaasPlanDescriptor[] | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    getBillingStatus()
      .then(setBilling)
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : 'unknown';
        setLoadError(`Failed to load billing status (${code})`);
      });
    getSaasPlans()
      .then((res) => {
        setPlans(res.plans);
        setConfigured(res.configured);
      })
      .catch(() => {
        // Plans are optional; the UI still shows current status.
        setPlans([]);
        setConfigured(false);
      });
  }, []);

  const handleSubscribe = async (priceId: string) => {
    setActionError(null);
    setActionLoading(true);
    try {
      const { url } = await startCheckout(
        priceId,
        `${window.location.origin}/settings/billing?success=1`,
        `${window.location.origin}/settings/billing`,
      );
      window.location.href = url;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setActionError(`Could not start checkout (${code})`);
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    setActionError(null);
    setActionLoading(true);
    try {
      const { url } = await openCustomerPortal(`${window.location.origin}/settings/billing`);
      window.location.href = url;
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setActionError(`Could not open billing portal (${code})`);
      setActionLoading(false);
    }
  };

  // Match current plan by priceId so we can highlight it in the picker.
  const currentPriceId = billing?.planPriceId ?? null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a plan and manage your subscription. Pricing is per unit, where{' '}
          <span className="num">1</span> boarding run = <span className="num">4</span> daycare slots
          = <span className="num">1</span> grooming station.
        </p>
      </div>

      {loadError && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {loadError}
        </p>
      )}

      {actionError && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {actionError}
        </p>
      )}

      {billing ? (
        <CurrentStatusCard billing={billing} loading={actionLoading} onManage={handleManage} />
      ) : !loadError ? (
        <div className="rounded-xl border border-border bg-surface p-6">
          <p className="text-sm text-muted-foreground">Loading billing status…</p>
        </div>
      ) : null}

      {/* Plan picker — only shown when subscription is not active OR is canceled.
          For active subs the operator uses the Stripe Customer Portal to change. */}
      {billing && billing.status !== 'active' && plans && plans.length > 0 && (
        <section>
          <h2 className="mb-4 font-heading text-lg font-medium tracking-tight">Choose a plan</h2>
          {configured === false && (
            <div className="mb-4 rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 text-sm text-warning">
              Subscription is not configured yet — the platform operator must set the{' '}
              <code className="font-mono text-xs">STRIPE_PRICE_STARTER</code> /
              <code className="font-mono text-xs"> _PRO</code> /
              <code className="font-mono text-xs"> _ENTERPRISE</code> secrets in Wrangler. Until
              then, plans render but cannot be subscribed to.
            </div>
          )}
          <div className="grid gap-4 lg:grid-cols-3">
            {plans.map((p) => (
              <PlanCard
                key={p.tier}
                plan={p}
                isCurrent={currentPriceId !== null && currentPriceId === p.priceId}
                onSubscribe={handleSubscribe}
                loading={actionLoading}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
